/**
 * §P.10 · Los campeonatos.
 *
 * ## Los ajenos se ven y no se tocan
 *
 * `is_mine` separa lo que organiza este club de lo que organiza la federación y está
 * publicado. Los segundos son a los que el club **lleva** alumnos, que es el caso
 * normal: casi ningún club organiza campeonatos, y todos van a ellos.
 *
 * Editar uno ajeno no se ofrece. No es que el servidor lo rechace y aquí se adelante:
 * es que cambiar la fecha de un campeonato de la federación desde el panel de un club
 * no significa nada.
 *
 * ## La fecha límite de inscripción se enseña siempre
 *
 * Pasado el plazo, convocar responde `409 registration_deadline_passed`, y no es una
 * formalidad: la federación no admite la inscripción. Un club que ve «quedan 3 días»
 * en la lista convoca el martes; uno que no lo ve, se entera el sábado.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, fecha } from '@/shared/ui/formato';
import { FaltaAntes } from '@/shared/ui/FaltaAntes';
import type { DisciplinaDelClub } from '@/club/ajustes/tipos';
import {
  etiquetaDeNivel,
  NIVELES,
  type Campeonato,
  type NivelDeCampeonato,
} from '@/club/competicion/tipos';

interface Campos {
  discipline_id: string;
  name: string;
  starts_on: string;
  city: string;
  venue_name: string;
  federation: string;
  level: string;
  registration_deadline: string;
}

const VACIO: Campos = {
  discipline_id: '',
  name: '',
  starts_on: '',
  city: '',
  venue_name: '',
  federation: '',
  level: 'provincial',
  registration_deadline: '',
};

const oNulo = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** «Quedan 3 días para inscribir» / «El plazo se cerró». */
function plazo(limite: string | Date | null): string | null {
  if (limite === null) return null;

  const dia = new Date(limite);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const dias = Math.round((dia.getTime() - hoy.getTime()) / 86_400_000);

  if (dias < 0) return 'El plazo de inscripción se cerró';
  if (dias === 0) return 'Último día para inscribir';
  if (dias === 1) return 'Queda 1 día para inscribir';
  return `Quedan ${dias} días para inscribir`;
}

export function Campeonatos({
  club,
  puedeEditar,
  alAbrir,
}: {
  club: string;
  puedeEditar: boolean;
  alAbrir: (id: string) => void;
}) {
  const cache = useQueryClient();
  const [soloMios, setSoloMios] = useState(false);
  const [creando, setCreando] = useState(false);
  const [campos, setCampos] = useState<Campos>(VACIO);
  const [problema, setProblema] = useState<Problema | null>(null);

  const campeonatos = useQuery({
    queryKey: ['campeonatos', club, soloMios],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).competitions.get({
        query: soloMios ? { only_mine: true } : {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Campeonato[];
    },
  });

  const disciplinas = useQuery({
    queryKey: ['disciplinas-del-club', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).disciplines.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DisciplinaDelClub[];
    },
  });

  const crear = useMutation({
    mutationFn: async (c: Campos) => {
      const r = await api.v1.clubs({ club }).competitions.post({
        discipline_id: c.discipline_id,
        name: c.name.trim(),
        starts_on: c.starts_on,
        city: oNulo(c.city),
        venue_name: oNulo(c.venue_name),
        federation: oNulo(c.federation),
        level: (oNulo(c.level) as NivelDeCampeonato | null) ?? null,
        registration_deadline: oNulo(c.registration_deadline),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setCreando(false);
      setCampos(VACIO);
      setProblema(null);
      void cache.invalidateQueries({ queryKey: ['campeonatos', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (campeonatos.isPending) {
    return <Cargando filas={3} etiqueta="Cargando los campeonatos" />;
  }

  if (campeonatos.isError || !campeonatos.data) {
    return (
      <PanelError
        problema={aProblema(campeonatos.error)}
        reintentar={() => void campeonatos.refetch()}
      />
    );
  }

  return (
    <section>
      <header className="titulo-fila">
        <h1>Campeonatos</h1>
        <div className="acciones">
          <button
            type="button"
            className={soloMios ? 'pestaña-activa' : 'btn-plano'}
            onClick={() => setSoloMios(!soloMios)}
          >
            {soloMios ? 'Ver todos' : 'Solo los que organizo'}
          </button>
          {puedeEditar && !creando && (
            <button
              type="button"
              className="btn-primario"
              onClick={() => {
                setCampos({
                  ...VACIO,
                  discipline_id: disciplinas.data?.[0]?.discipline_id ?? '',
                });
                setCreando(true);
              }}
            >
              Añadir campeonato
            </button>
          )}
        </div>
      </header>

      {problema && <PanelError problema={problema} />}

      {creando && (
        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            setProblema(null);
            crear.mutate(campos);
          }}
        >
          <div className="rejilla">
            <label>
              Cómo se llama
              <input
                required
                maxLength={160}
                placeholder="Open de Madrid infantil"
                value={campos.name}
                onChange={(e) => setCampos({ ...campos, name: e.target.value })}
              />
            </label>

            <label>
              Disciplina
              <select
                required
                value={campos.discipline_id}
                onChange={(e) =>
                  setCampos({ ...campos, discipline_id: e.target.value })
                }
              >
                <option value="">Elige</option>
                {(disciplinas.data ?? []).map((d) => (
                  <option key={d.discipline_id} value={d.discipline_id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>

            {disciplinas.data?.length === 0 && (
              <FaltaAntes
                accion="Elegir la disciplina"
                destino="/club/disciplinas"
              >
                Tu club no tiene todavía ninguna disciplina, y cada campeonato
                es de una.
              </FaltaAntes>
            )}

            <label>
              Cuándo
              <input
                required
                type="date"
                value={campos.starts_on}
                onChange={(e) =>
                  setCampos({ ...campos, starts_on: e.target.value })
                }
              />
            </label>

            <label>
              Hasta cuándo se puede inscribir
              <input
                type="date"
                value={campos.registration_deadline}
                onChange={(e) =>
                  setCampos({
                    ...campos,
                    registration_deadline: e.target.value,
                  })
                }
              />
            </label>

            <label>
              Ciudad
              <input
                maxLength={100}
                placeholder="Alcalá de Henares"
                value={campos.city}
                onChange={(e) => setCampos({ ...campos, city: e.target.value })}
              />
            </label>

            <label>
              Pabellón
              <input
                maxLength={160}
                placeholder="Polideportivo Municipal"
                value={campos.venue_name}
                onChange={(e) =>
                  setCampos({ ...campos, venue_name: e.target.value })
                }
              />
            </label>

            <label>
              Federación
              <input
                maxLength={40}
                placeholder="FMTKD"
                value={campos.federation}
                onChange={(e) =>
                  setCampos({ ...campos, federation: e.target.value })
                }
              />
            </label>

            <label>
              Nivel
              <select
                value={campos.level}
                onChange={(e) =>
                  setCampos({ ...campos, level: e.target.value })
                }
              >
                {NIVELES.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.etiqueta}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="acciones">
            <button
              type="submit"
              className="btn-primario"
              disabled={crear.isPending}
            >
              {crear.isPending ? 'Creando…' : 'Crear'}
            </button>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setCreando(false)}
            >
              Dejarlo
            </button>
          </div>
        </form>
      )}

      {campeonatos.data.length === 0 ? (
        <Vacio
          titulo={
            soloMios ? 'No organizas ninguno' : 'No hay campeonatos a la vista'
          }
          explicacion="Añade el que viene y podrás convocar a quien lleves, con la categoría que le toca por edad y peso calculada sola."
        />
      ) : (
        <ul className="papeles">
          {campeonatos.data.map((c) => {
            const aviso = plazo(c.registration_deadline);
            const cerrado = aviso === 'El plazo de inscripción se cerró';

            return (
              <li key={c.id}>
                <div className="aviso-cabeza">
                  <button
                    type="button"
                    className="enlace-fila"
                    onClick={() => alAbrir(c.id)}
                  >
                    <strong>{c.name}</strong>
                  </button>
                  <span className="pastilla pastilla-left">
                    {etiquetaDeNivel(c.level)}
                  </span>
                  {!c.is_mine && (
                    <span className="pastilla pastilla-left">
                      Lo organiza otro
                    </span>
                  )}
                  {c.medals > 0 && (
                    <span className="pastilla pastilla-bien">
                      {cuenta(c.medals, 'medalla', 'medallas')}
                    </span>
                  )}
                </div>

                <p className="tenue">
                  {fecha(c.starts_on)}
                  {c.city && ` · ${c.city}`}
                  {c.venue_name && ` · ${c.venue_name}`} · {c.discipline_name}
                  {c.federation && ` · ${c.federation}`}
                </p>

                <p className="tenue">
                  {cuenta(c.registered_count, 'convocado', 'convocados')}
                  {aviso && (
                    <>
                      {' · '}
                      {/*
                        En negrita cuando queda poco o se pasó: es la diferencia
                        entre convocar el martes y enterarse el sábado en la mesa
                        de inscripciones.
                      */}
                      <strong className={cerrado ? 'texto-mal' : undefined}>
                        {aviso}
                      </strong>
                    </>
                  )}
                </p>

                <div className="acciones">
                  <button
                    type="button"
                    className="btn-plano"
                    onClick={() => alAbrir(c.id)}
                  >
                    Abrir
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
